import React, { useState, useEffect } from 'react';
import { ChevronRight, Clock, Pencil, XCircle } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { useTheme } from '../../ThemeContext';
import { AlertBox, FormLabel, FormInput } from '../../components/ui';


export default function AuthorSettingsView({
  selectedAuthor,
  setView,
  setCurrentPost,
  onAuthorUpdated,
  onAuthorDeleted
}) {
  const { t, isDark } = useTheme();
  const [name, setName] = useState('');
  const [handle, setHandle] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [location, setLocation] = useState('');
  const [bio, setBio] = useState('');
  const [originalHandle, setOriginalHandle] = useState('');
  const [handleAvailable, setHandleAvailable] = useState(null);
  const [checkingHandle, setCheckingHandle] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('profile');

  // Scheduled posts state
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [loadingScheduled, setLoadingScheduled] = useState(false);

  // Avatar state
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarError, setAvatarError] = useState('');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // Load current author data
  useEffect(() => {
    if (selectedAuthor) {
      setName(selectedAuthor.name || '');
      setEmail(selectedAuthor.email || '');
      setWebsite(selectedAuthor.website || '');
      setLocation(selectedAuthor.location || '');
      setBio(selectedAuthor.bio || '');
      
      // Load handle from entity_handles table
      loadHandle();
    }
  }, [selectedAuthor]);

  const loadHandle = async () => {
    if (!selectedAuthor?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('entity_handles')
        .select('handle')
        .eq('entity_type', 'individual')
        .eq('entity_id', selectedAuthor.id)
        .single();
      
      if (!error && data) {
        setHandle(data.handle);
        setOriginalHandle(data.handle);
      }
    } catch (err) {
      console.error('Error loading handle:', err);
    }
  };

  // Load scheduled posts
  useEffect(() => {
    if (selectedAuthor?.id && activeTab === 'scheduled') {
      loadScheduledPosts();
    }
  }, [selectedAuthor?.id, activeTab]);

  const loadScheduledPosts = async () => {
    if (!selectedAuthor?.id) return;
    setLoadingScheduled(true);
    try {
      const { data, error } = await supabase
        .from('posts')
        .select('id, title, category, scheduled_at')
        .eq('author_id', selectedAuthor.id)
        .gt('scheduled_at', Date.now())
        .order('scheduled_at', { ascending: true });

      if (!error) {
        setScheduledPosts(data || []);
      }
    } catch (err) {
      console.error('Error loading scheduled posts:', err);
    }
    setLoadingScheduled(false);
  };

  const cancelScheduledPost = async (postId) => {
    const confirmed = window.confirm('Cancel this scheduled post? It will return to drafts.');
    if (!confirmed) return;

    try {
      const { error } = await supabase
        .from('posts')
        .update({ scheduled_at: null })
        .eq('id', postId);

      if (error) throw error;
      setScheduledPosts(prev => prev.filter(p => p.id !== postId));
    } catch (err) {
      console.error('Error cancelling scheduled post:', err);
    }
  };

  const editScheduledPost = async (postId) => {
    try {
      const { data, error } = await supabase
        .from('posts')
        .select('*')
        .eq('id', postId)
        .single();

      if (error) throw error;
      if (setCurrentPost) setCurrentPost(data);
      setView('editor');
    } catch (err) {
      console.error('Error loading post for edit:', err);
    }
  };

  // Avatar file selection and validation
  const handleAvatarSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Reset errors
    setAvatarError('');

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      setAvatarError('Please upload a PNG or JPG image');
      return;
    }

    // Validate file size (2MB max)
    const maxSize = 2 * 1024 * 1024; // 2MB in bytes
    if (file.size > maxSize) {
      setAvatarError('Image must be less than 2MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result);
      setAvatarFile(file);
    };
    reader.readAsDataURL(file);
  };

  // Remove avatar
  const handleRemoveAvatar = async () => {
    // If there's just a preview (not saved yet), just clear it
    if (avatarPreview && !selectedAuthor.avatar_url) {
      setAvatarFile(null);
      setAvatarPreview(null);
      setAvatarError('');
      return;
    }

    // Confirm deletion if avatar is saved
    if (selectedAuthor.avatar_url) {
      const confirmed = window.confirm('Remove your profile picture?');
      if (!confirmed) return;

      setIsSubmitting(true);
      try {
        // Extract filename from URL to delete from storage
        if (selectedAuthor.avatar_url.includes('author-avatars/')) {
          const urlParts = selectedAuthor.avatar_url.split('author-avatars/');
          const fileName = urlParts[1];
          
          // Delete from storage
          await supabase.storage
            .from('author-avatars')
            .remove([`avatars/${fileName}`]);
        }

        // Update database to remove avatar_url
        const { error } = await supabase
          .from('authors')
          .update({ avatar_url: null })
          .eq('id', selectedAuthor.id);

        if (error) throw error;

        // Clear local state
        setAvatarFile(null);
        setAvatarPreview(null);
        setAvatarError('');
        
        // Reload author data
        if (onAuthorUpdated) {
          await onAuthorUpdated();
        }

        setSuccess('Profile picture removed');
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        console.error('Error removing avatar:', err);
        setError('Failed to remove profile picture');
      }
      setIsSubmitting(false);
    } else {
      // Just clear the preview
      setAvatarFile(null);
      setAvatarPreview(null);
      setAvatarError('');
    }
  };

  // Upload avatar to Supabase Storage
  const uploadAvatar = async () => {
    if (!avatarFile) return null;

    setUploadingAvatar(true);
    try {
      // Create unique filename
      const fileExt = avatarFile.name.split('.').pop();
      const fileName = `${selectedAuthor.id}-${Date.now()}.${fileExt}`;
      const filePath = `avatars/${fileName}`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('author-avatars')
        .upload(filePath, avatarFile, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('author-avatars')
        .getPublicUrl(filePath);

      setUploadingAvatar(false);
      return urlData.publicUrl;
    } catch (err) {
      console.error('Error uploading avatar:', err);
      setAvatarError('Failed to upload image. Please try again.');
      setUploadingAvatar(false);
      return null;
    }
  };

  // Check handle availability only if changed
  useEffect(() => {
    if (!handle.trim() || handle === originalHandle) {
      setHandleAvailable(null);
      return;
    }

    const checkHandle = async () => {
      setCheckingHandle(true);
      try {
        const { data, error } = await supabase
          .from('entity_handles')
          .select('handle')
          .eq('handle', handle.toLowerCase())
          .single();

        if (error && error.code === 'PGRST116') {
          setHandleAvailable(true);
        } else if (data) {
          setHandleAvailable(false);
        }
      } catch (err) {
        console.error('Error checking handle:', err);
      }
      setCheckingHandle(false);
    };

    const debounce = setTimeout(checkHandle, 500);
    return () => clearTimeout(debounce);
  }, [handle, originalHandle]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (!handle.trim()) {
      setError('Handle is required');
      return;
    }
    if (handle !== originalHandle && handleAvailable === false) {
      setError('Handle is already taken');
      return;
    }
    if (bio.length > 200) {
      setError('Bio must be 200 characters or less');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      // Upload avatar if new file selected
      let avatarUrl = selectedAuthor.avatar_url;
      if (avatarFile) {
        const uploadedUrl = await uploadAvatar();
        if (uploadedUrl) {
          avatarUrl = uploadedUrl;
        } else {
          // If avatar upload fails, stop and show error
          setIsSubmitting(false);
          return;
        }
      }

      // Update author
      const { error: authorError } = await supabase
        .from('authors')
        .update({
          name: name.trim(),
          bio: bio.trim() || null,
          email: email.trim() || null,
          website: website.trim() || null,
          location: location.trim() || null,
          avatar_url: avatarUrl
        })
        .eq('id', selectedAuthor.id);

      if (authorError) throw authorError;

      // Update handle if changed
      if (handle !== originalHandle) {
        const { error: handleError } = await supabase
          .from('entity_handles')
          .update({
            handle: handle.toLowerCase()
          })
          .eq('entity_type', 'individual')
          .eq('entity_id', selectedAuthor.id);

        if (handleError) throw handleError;
        setOriginalHandle(handle);
      }

      setSuccess('Settings saved successfully!');
      
      // Clear avatar file state after successful upload
      setAvatarFile(null);
      setAvatarPreview(null);
      
      // Reload the author data to get the new avatar URL
      if (selectedAuthor?.id) {
        const { data: updatedAuthor } = await supabase
          .from('authors')
          .select('*')
          .eq('id', selectedAuthor.id)
          .single();
        
        if (updatedAuthor && onAuthorUpdated) {
          // This will trigger App.jsx to reload authors
          onAuthorUpdated();
        }
      }
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating author:', err);
      setError('Failed to save settings. Please try again.');
    }
    
    setIsSubmitting(false);
  };

  const handleDelete = async () => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${selectedAuthor.name}"? This action cannot be undone.`
    );
    
    if (!confirmDelete) return;

    setIsSubmitting(true);
    setError('');

    try {
      // Delete handle first (foreign key constraint)
      const { error: handleError } = await supabase
        .from('entity_handles')
        .delete()
        .eq('entity_type', 'individual')
        .eq('entity_id', selectedAuthor.id);

      if (handleError) throw handleError;

      // Delete author
      const { error: authorError } = await supabase
        .from('authors')
        .delete()
        .eq('id', selectedAuthor.id);

      if (authorError) throw authorError;

      // Success - navigate back
      if (onAuthorDeleted) {
        onAuthorDeleted();
      }
      setView('profile');
    } catch (err) {
      console.error('Error deleting author:', err);
      setError('Failed to delete author. Please try again.');
      setIsSubmitting(false);
    }
  };

  if (!selectedAuthor) return null;

  return (
    <div className="max-w-2xl mx-auto px-6 py-12">
      {/* Back Button */}
      <button
        onClick={() => setView('authorProfile')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          color: t.text,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: '16px',
          fontFamily: 'Poppins, sans-serif',
          fontWeight: '500',
          padding: '0',
          marginBottom: '40px'
        }}
      >
        <ChevronRight size={20} style={{ transform: 'rotate(180deg)' }} />
        Return to Profile
      </button>

      {/* Settings Form */}
      <div style={{
        backgroundColor: t.surface,
        border: `1px solid ${t.borderStrong}`,
        borderRadius: '12px',
        padding: '32px'
      }}>
        <h1 style={{
          fontSize: '28px',
          fontWeight: '600',
          marginBottom: '8px',
          fontFamily: 'Poppins, sans-serif',
          color: t.text
        }}>
          Author Settings
        </h1>
        <p style={{
          fontSize: '14px',
          color: t.textSecondary,
          marginBottom: '24px',
          fontFamily: 'Poppins, sans-serif'
        }}>
          Update your author profile information
        </p>

        {/* Tabs */}
        <div style={{
          display: 'flex', gap: 0, marginBottom: '32px',
          borderBottom: `1px solid ${t.borderStrong}`,
        }}>
          {['profile', 'scheduled'].map(tab => {
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  padding: '10px 20px',
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '14px',
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? t.text : t.textSecondary,
                  background: 'none',
                  border: 'none',
                  borderBottom: isActive ? `2px solid ${t.accent}` : '2px solid transparent',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  textTransform: 'capitalize',
                }}
              >
                {tab === 'scheduled' && <Clock size={14} style={{ marginRight: 6, verticalAlign: '-2px' }} />}
                {tab}
              </button>
            );
          })}
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (<>

        {/* Success Message */}
        {success && (
          <AlertBox variant="success">{success}</AlertBox>
        )}

        {/* Error Message */}
        {error && (
          <AlertBox>{error}</AlertBox>
        )}

        {/* Avatar Upload Section */}
        <div style={{
          display: 'flex',
          gap: '32px',
          marginBottom: '32px',
          paddingBottom: '32px',
          borderBottom: '1px solid #e7e5e4'
        }}>
          {/* Avatar Preview */}
          <div style={{ flexShrink: 0 }}>
            {selectedAuthor.avatar_url || avatarPreview ? (
              <img
                src={avatarPreview || selectedAuthor.avatar_url}
                alt={selectedAuthor.name}
                style={{
                  width: '120px',
                  height: '120px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: `2px solid ${t.borderStrong}`
                }}
              />
            ) : (
              <div style={{
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                backgroundColor: t.buttonBg,
                color: t.buttonText,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '48px',
                fontWeight: '600',
                fontFamily: 'Poppins, sans-serif',
                border: `2px solid ${t.borderStrong}`
              }}>
                {name ? name[0].toUpperCase() : 'A'}
              </div>
            )}
          </div>

          {/* Upload Controls */}
          <div style={{ flex: 1 }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              marginBottom: '8px',
              fontFamily: 'Poppins, sans-serif',
              color: t.text
            }}>
              Profile Picture
            </h3>
            <p style={{
              fontSize: '13px',
              color: t.textSecondary,
              marginBottom: '16px',
              fontFamily: 'Poppins, sans-serif',
              lineHeight: '1.5'
            }}>
              Upload a square image (PNG or JPG). Max 2MB. Image will be automatically cropped to fit.
            </p>

            {/* File Input */}
            <input
              type="file"
              accept="image/png, image/jpeg, image/jpg"
              onChange={handleAvatarSelect}
              style={{ display: 'none' }}
              id="avatar-upload"
            />
            
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <label
                htmlFor="avatar-upload"
                style={{
                  display: 'inline-block',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  border: `1px solid ${t.borderStrong}`,
                  backgroundColor: 'transparent',
                  color: t.text,
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  fontFamily: 'Poppins, sans-serif',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = t.accent;
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = t.borderStrong;
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {avatarFile ? 'Change Image' : 'Upload Image'}
              </label>

              {(avatarFile || selectedAuthor.avatar_url) && (
                <button
                  onClick={handleRemoveAvatar}
                  disabled={isSubmitting}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '6px',
                    border: `1px solid ${t.error}`,
                    backgroundColor: 'transparent',
                    color: t.error,
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: 'Poppins, sans-serif',
                    transition: 'all 0.2s',
                    opacity: isSubmitting ? 0.5 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!isSubmitting) {
                      e.currentTarget.style.backgroundColor = t.error;
                      e.currentTarget.style.color = t.surface;
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = t.error;
                  }}
                >
                  Remove
                </button>
              )}
            </div>

            {/* Upload Validation Feedback */}
            {avatarError && (
              <p style={{
                fontSize: '12px',
                color: t.error,
                marginTop: '8px',
                fontFamily: 'Poppins, sans-serif'
              }}>
                {avatarError}
              </p>
            )}
            {avatarFile && !avatarError && (
              <p style={{
                fontSize: '12px',
                color: t.success,
                marginTop: '8px',
                fontFamily: 'Poppins, sans-serif'
              }}>
                ✓ Ready to upload: {avatarFile.name}
              </p>
            )}
          </div>
        </div>

        {/* Name - Required */}
        <div style={{ marginBottom: '20px' }}>
          <FormLabel>Name *</FormLabel>
          <FormInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your author name"
          />
        </div>

        {/* Handle - Required with availability check */}
        <div style={{ marginBottom: '20px' }}>
          <FormLabel>Handle * <span style={{ color: t.textSecondary, fontWeight: '400' }}>(@{handle || 'yourhandle'})</span></FormLabel>
          <input
            type="text"
            value={handle}
            onChange={(e) => setHandle(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            placeholder="unique-handle"
            style={{
              width: '100%',
              padding: '12px',
              border: `1px solid ${handleAvailable === false ? t.error : handleAvailable === true ? t.success : t.borderStrong}`,
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'Poppins, sans-serif',
              backgroundColor: t.surface,
              outline: 'none'
            }}
            onFocus={(e) => {
              if (handleAvailable !== false && handleAvailable !== true) {
                e.target.style.borderColor = t.accent;
              }
            }}
            onBlur={(e) => {
              if (handleAvailable !== false && handleAvailable !== true) {
                e.target.style.borderColor = t.borderStrong;
              }
            }}
          />
          {checkingHandle && (
            <p style={{ fontSize: '12px', color: t.textSecondary, marginTop: '4px', fontFamily: 'Poppins, sans-serif' }}>
              Checking availability...
            </p>
          )}
          {!checkingHandle && handleAvailable === true && (
            <p style={{ fontSize: '12px', color: t.success, marginTop: '4px', fontFamily: 'Poppins, sans-serif' }}>
              ✓ Handle is available
            </p>
          )}
          {!checkingHandle && handleAvailable === false && (
            <p style={{ fontSize: '12px', color: t.error, marginTop: '4px', fontFamily: 'Poppins, sans-serif' }}>
              ✗ Handle is already taken
            </p>
          )}
        </div>

        {/* Email - Optional */}
        <div style={{ marginBottom: '20px' }}>
          <FormLabel>Email</FormLabel>
          <FormInput
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
          />
        </div>

        {/* Website - Optional */}
        <div style={{ marginBottom: '20px' }}>
          <FormLabel>Website</FormLabel>
          <FormInput
            type="url"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            placeholder="https://yourwebsite.com"
          />
        </div>

        {/* Location - Optional */}
        <div style={{ marginBottom: '20px' }}>
          <FormLabel>Location</FormLabel>
          <FormInput
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Earth, Brooklyn, Cyberspace..."
          />
        </div>

        {/* Bio - Optional with character limit */}
        <div style={{ marginBottom: '32px' }}>
          <FormLabel>Bio <span style={{ color: t.textSecondary, fontWeight: '400' }}>({bio.length}/200)</span></FormLabel>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="Brief description..."
            rows={3}
            maxLength={200}
            style={{
              width: '100%',
              padding: '12px',
              border: `1px solid ${t.borderStrong}`,
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'Poppins, sans-serif',
              backgroundColor: t.surface,
              outline: 'none',
              resize: 'vertical'
            }}
            onFocus={(e) => e.target.style.borderColor = t.accent}
            onBlur={(e) => e.target.style.borderColor = t.borderStrong}
          />
        </div>

        {/* Save Button */}
        <button
          onClick={handleSubmit}
          disabled={!name.trim() || !handle.trim() || (handle !== originalHandle && handleAvailable === false) || checkingHandle || isSubmitting || uploadingAvatar}
          style={{
            width: '100%',
            backgroundColor: t.buttonBg,
            color: t.buttonText,
            padding: '12px',
            borderRadius: '12px',
            border: 'none',
            cursor: (!name.trim() || !handle.trim() || (handle !== originalHandle && handleAvailable === false) || checkingHandle || isSubmitting || uploadingAvatar) ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            fontFamily: 'Poppins, sans-serif',
            opacity: (!name.trim() || !handle.trim() || (handle !== originalHandle && handleAvailable === false) || checkingHandle || isSubmitting || uploadingAvatar) ? 0.5 : 1,
            marginBottom: '24px',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            if (!isSubmitting && !uploadingAvatar && name.trim() && handle.trim()) {
              e.target.style.transform = 'translateY(-2px)';
            }
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
          }}
        >
          {uploadingAvatar ? 'Uploading Image...' : isSubmitting ? 'Saving...' : 'Save Changes'}
        </button>

        {/* Danger Zone - Delete */}
        <div style={{
          borderTop: '1px solid #e7e5e4',
          paddingTop: '24px'
        }}>
          <h3 style={{
            fontSize: '16px',
            fontWeight: '600',
            marginBottom: '8px',
            fontFamily: 'Poppins, sans-serif',
            color: t.text
          }}>
            Danger Zone
          </h3>
          <p style={{
            fontSize: '13px',
            color: t.textSecondary,
            marginBottom: '16px',
            fontFamily: 'Poppins, sans-serif'
          }}>
            Once you delete an author, there is no going back. Please be certain.
          </p>
          <button
            onClick={handleDelete}
            disabled={isSubmitting}
            style={{
              width: '100%',
              backgroundColor: 'transparent',
              color: t.error,
              padding: '12px',
              borderRadius: '6px',
              border: `1px solid ${t.error}`,
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              fontFamily: 'Poppins, sans-serif',
              opacity: isSubmitting ? 0.5 : 1,
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) {
                e.target.style.backgroundColor = t.error;
                e.target.style.color = t.surface;
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = t.error;
            }}
          >
            Delete Author
          </button>
        </div>

        </>)}

        {/* Scheduled Tab */}
        {activeTab === 'scheduled' && (
          <div>
            {loadingScheduled ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div className="inline-block w-6 h-6 border-4 border-stone-300 rounded-full animate-spin" style={{ borderTopColor: t.accent }} />
              </div>
            ) : scheduledPosts.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {scheduledPosts.map((post, idx) => {
                  const d = new Date(post.scheduled_at);
                  return (
                    <div
                      key={post.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 16,
                        padding: '16px', borderRadius: 8,
                        border: `1px solid ${t.borderStrong}`,
                        backgroundColor: t.surface,
                      }}
                    >
                      {/* Number */}
                      <span style={{
                        width: 28, height: 28, borderRadius: 999,
                        backgroundColor: t.buttonBg, color: t.buttonText,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 700, fontFamily: 'Poppins, sans-serif',
                        flexShrink: 0,
                      }}>
                        {idx + 1}
                      </span>

                      {/* Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 14, fontWeight: 600, color: t.text,
                          fontFamily: 'Poppins, sans-serif',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                          {post.title}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                          <span style={{
                            display: 'inline-block', padding: '2px 10px', borderRadius: 999,
                            border: `1px solid ${t.borderStrong}`, fontSize: 11, fontWeight: 500,
                            color: t.textSecondary, fontFamily: 'Poppins, sans-serif',
                            textTransform: 'uppercase', letterSpacing: 0.5,
                          }}>
                            {post.category}
                          </span>
                          <span style={{ fontSize: 12, color: t.textSecondary, fontFamily: 'Poppins, sans-serif' }}>
                            {d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} at {d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                        <button
                          onClick={() => editScheduledPost(post.id)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 4,
                            padding: '6px 12px', borderRadius: 6,
                            border: `1px solid ${t.borderStrong}`,
                            background: 'none', color: t.text, cursor: 'pointer',
                            fontSize: 12, fontWeight: 500, fontFamily: 'Poppins, sans-serif',
                            transition: 'all 0.15s',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.borderColor = t.accent; }}
                          onMouseLeave={(e) => { e.currentTarget.style.borderColor = t.borderStrong; }}
                        >
                          <Pencil size={12} /> Edit
                        </button>
                        <button
                          onClick={() => cancelScheduledPost(post.id)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 4,
                            padding: '6px 12px', borderRadius: 6,
                            border: `1px solid ${t.error}`,
                            background: 'none', color: t.error, cursor: 'pointer',
                            fontSize: 12, fontWeight: 500, fontFamily: 'Poppins, sans-serif',
                            transition: 'all 0.15s',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = t.error;
                            e.currentTarget.style.color = t.surface;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = t.error;
                          }}
                        >
                          <XCircle size={12} /> Cancel
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{
                textAlign: 'center', padding: '60px 20px',
                color: t.textSecondary, fontFamily: 'Poppins, sans-serif',
              }}>
                <Clock size={40} style={{ margin: '0 auto 16px', opacity: 0.3, color: t.text }} />
                <p style={{ fontSize: 14, lineHeight: 1.6 }}>
                  No posts scheduled. Use the Schedule toggle in the editor to queue a post for future publishing.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
